<template>
  <div class="w-full min-h-dvh sm:h-dvh flex flex-col app-style sm:min-h-568 text-xs min-w-[375px]"
  :class="[{dark:darkMode},scrollBars ? darkMode ? 'scroll-on-dark' :'scroll-on' : 'scroll-off']">
  <!-- Global Error Overlay -->
    <div v-if="appError" class="w-screen h-screen fixed inset-0 app-style bg-neutral-100 dark:bg-neutral-900 z-[200] flex flex-col items-center justify-center p-4">
      <div class="max-w-xl w-full flex flex-col items-center gap-2 py-8 px-6 rounded-lg border border-red-500/50 shadow-xl">
        <div class="w-full flex justify-center items-center gap-2 text-red">
          <ExclamationCircleIcon class="w-6 h-6 flex-shrink-0" />
          <h1 class="text-base font-semibold">Something went wrong</h1>
        </div>
        <div class="w-full text-center sm:text-sm whitespace-normal truncate p-1">
          An unexpected error occurred. You can go back or reload the page to continue.
        </div>
        <div v-if="appErrorMessage" class="break-all text-center sm:text-sm w-full max-h-24 overflow-y-auto whitespace-normal truncate text-indigo">
          {{ appErrorMessage }}
        </div>
        <div class="w-full flex flex-row gap-2 mt-2 justify-center">
          <button type="button" class="mr-2" @click="clearAppErrorAndGoBack">
            Go back
          </button>
          <button type="button" class="bn-text px-3 py-1" @click="clearAppErrorAndReload">
            Reload page
          </button>
        </div>
      </div>
    </div>
  <!-- Logout and Token Expiration -->
    <div v-if="loggingOut" class="w-screen h-screen absolute app-style bg-neutral-100 dark:bg-neutral-900 z-100">
      <div class="w-full h-full flex items-center justify-center">
        <LoadingData :size="'large'" :message="'Logging Out.'" />
      </div>
    </div>
    <!--<TokenExpiration />-->
  <!-- Top Navbar -->
    <div v-if="authenticated.loggedIn" class="sticky top-0 w-full z-[85] no-print">
      <div class="topbar">
      <!-- Menu toggle and status -->
        <div class="topbar-side">
          <button type="button" class="topbar-btn topbar-toggle" :aria-label="sideBar ? 'Close menu' : 'Open menu'"
            :aria-expanded="sideBar" @click="toggleSideBar"><ArrowCircleRightIcon /></button>
          <StatusAndOptionsMenu />
        </div>
      <!-- Centre Logo -->
        <router-link to="/" aria-label="Home" class="topbar-logo">
          <img src="./assets/Logo_long.png" alt="" class="hidden md:block w-40 lg:w-56 h-auto">
          <img src="./assets/favicon.png" alt="" class="block md:hidden w-[30px] h-[30px] object-contain">
        </router-link>
      <!-- Queues -->
        <!--Warehouse and Request Inboxes -->
          <!--<div v-if="authenticated.organization?.orgType?.includes('primary')" class="flex flex-row items-center justify-center px-2">
            <div v-for="(queue,index) in queues" :key="index">
              <div v-if="queue.condition" class="flex justify-center">
                <Popper hover arrow placement="bottom" :content="queue.popper ? queue.popper : null">
                  <router-link :to="queue.path" replace tabindex="-1">
                    <component :is="queue.component" />
                  </router-link>
                </Popper>
              </div>
            </div>
          </div>-->
      <!-- Search, scanner and account -->
        <div class="topbar-side">
          <Popper v-if="canSearchProjects" hover arrow placement="bottom" content="Project Search">
            <button type="button" class="topbar-btn" aria-label="Project Search" @click="showProjectSearch = true"><ClipboardSearchIcon /></button>
          </Popper>
          <Popper v-if="canSearchEquipment" hover arrow placement="bottom" content="Equipment Search">
            <button type="button" class="topbar-btn" aria-label="Equipment Search" @click="showEquipmentSearch = true"><BoxSearchIcon /></button>
          </Popper>
          <Popper v-if="isPrime" hover arrow placement="bottom" content="QR Code Scanner">
            <button type="button" class="topbar-btn" aria-label="QR Code Scanner" @click="showQRCodeScanner = true"><QrcodeIcon /></button>
          </Popper>
          <div v-if="canSearchProjects || canSearchEquipment || isPrime" class="topbar-rule" aria-hidden="true"></div>
          <AccountMenu />
        </div>
      </div>
    <!-- Planned sign-out countdown: one line (29px with its border) under the top bar, inside its block so the account menu drops over it; the side menu and task panel start below it -->
      <SignOutBar />
    </div>
  <!-- SideBar -->
    <!-- Phones: above a SidePanel (z-70), with the scrim between them; inert while closed so it is out of reach -->
    <nav v-if="authenticated.loggedIn" aria-label="Main menu" class="side-nav fixed top-12 bottom-0 left-0 z-80 sm:z-70 transform transition-transform duration-300 ease-in-out"
      :class="[sideBar ? 'translate-x-0 shadow-2xl sm:shadow-none dark:shadow-black/60' : '-translate-x-full', { '!top-[77px]': plannedSignOut }]"
      :inert="!sideBar"
    >
    <!-- Brand -->
      <div class="side-brand">
        <img src="./assets/dls-badge.svg" alt="" class="h-[26px] w-auto dark:hidden">
        <img src="./assets/dls-badge-reverse.svg" alt="" class="h-[26px] w-auto hidden dark:block">
        <DLSTrackerLogo class="h-[17px] w-auto text-neutral-500 dark:text-neutral-100" />
      </div>
    <!-- Sections -->
      <div class="side-scroll">
        <div v-for="section in sideBarSections" :key="section.label || 'none'">
          <div v-if="section.label" class="side-section">{{ section.label }}</div>
          <div class="side-list">
            <div v-for="item in section.items" :key="item.label">
            <!-- Group -->
              <button v-if="item.children" type="button" class="side-group" :class="{ 'has-current': groupHasCurrent(item) }" :aria-expanded="openGroups.has(item.label)" :tabindex="!sideBar ? '-1' : null" @click="toggleGroup(item.label)">
                <component :is="item.icon" /><span>{{ item.label }}</span><ChevronRightIcon class="side-chev" />
              </button>
              <div v-if="item.children && openGroups.has(item.label)" class="side-children">
                <router-link v-for="child in item.children" :key="child.path" class="side-sublink" :to="child.path" replace :tabindex="!sideBar ? '-1' : null" @click="closeSideBar">{{ child.label }}</router-link>
              </div>
            <!-- Link -->
              <router-link v-if="!item.children" class="side-link" :to="item.path" replace :tabindex="!sideBar ? '-1' : null" @click="closeSideBar">
                <component :is="item.icon" /><span>{{ item.label }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </nav>
    <div v-if="authenticated.loggedIn && sideBar" class="side-scrim z-[75] sm:hidden" @click="closeSideBar"></div>
  <!-- Main content displays router view -->
    <div :class="[
      'ease-in-out transition-all duration-300 flex-grow flex flex-col w-full min-h-0',
      authenticated.loggedIn && sideBar ? 'sm:pl-56' : ''
    ]" id="main">
  <!-- Top Logo -->
      <div v-if="authenticated.organization?.logoId && !hideHeader"
      class="lg:flex place-content-center absolute w-16 h-16 top-14 right-2 z-30 hidden"
      :class="sideBar ? 'ease-in-out transition-all duration-300 transform top-logo-bar': 'ease-in-out transition-all duration-300 transform pl-0'"
      >
        <div v-if="authenticated.organization?.logoId !== ''" class="w-24 flex flex-row items-center justify-center rounded-full">
          <img :src="`https://drive.google.com/thumbnail?authuser=0&size=h100&alt=media&id=${authenticated.organization.logoId}`" alt="DLS" onerror="this.onerror = null;this.src='/favicon.png';" class="object-contain align-middle border-none p-1" :style="'max-height:100px'" />
        </div>
      </div>

  <!-- Main Content -->
    <router-view :key="$route.fullPath" />
  <!-- Modals -->
      <teleport to="#modals" v-if="isLogoutError">
        <logout-modal />
      </teleport>
      <div id="modals"></div>
    </div>
    <Modal v-if="showQRCodeScanner && isPrime" :title="'QR Code Scanner'" @closeModal="showQRCodeScanner = false">
      <template v-slot:content>
        <QRCodeScanner />
      </template>
    </Modal>
    <Modal v-if="showProjectSearch && canSearchProjects" showFull :title="'Project Search'" @closeModal="showProjectSearch = false">
      <template v-slot:content>
        <ProjectSearchV2 />
      </template>
    </Modal>
    <Modal v-if="showEquipmentSearch && canSearchEquipment" showFull :title="'Equipment Search'" @closeModal="showEquipmentSearch = false">
      <template v-slot:content>
        <EquipmentSearch />
      </template>
    </Modal>
    
    <!-- Task Panel - Positioned at root level for absolute positioning -->
    <TaskPanel v-if="authenticated.loggedIn" />
  </div>
  
</template>

<script>
import { provide, ref,onBeforeUnmount, onMounted,computed, onBeforeMount, watch} from "vue";
import global from "./global";
import axios from "axios";
import { startDayChangeNotifier } from "@/components/utils/dayChangeNotifier";
import {useRoute, useRouter} from 'vue-router'
import {
  QrcodeIcon,
  DocumentTextIcon,
  ArrowCircleRightIcon,
  HomeIcon,
  ClipboardIcon,
  TruckIcon,
  SearchCircleIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentDuplicateIcon as DocumentsIcon,
  ServerIcon,
  BookOpenIcon,
  UsersIcon,
  OfficeBuildingIcon,
  CogIcon,
  UserGroupIcon,
  CashIcon,
  SearchIcon,
  LibraryIcon,
  PlusCircleIcon as PlusCircleIconOutline,
  UserCircleIcon,
  WifiIcon,
  ColorSwatchIcon,
  DocumentAddIcon
  } from "@heroicons/vue/outline"

import {ClipboardCheckIcon,UserGroupIcon as UserGroupSolidIcon,ChevronRightIcon,PlusCircleIcon, PlusIcon,OfficeBuildingIcon as OfficeBuildingSolidIcon} from '@heroicons/vue/solid'
import VanIcon from "@/components/customIcons/VanIcon.vue"
import WrenchIcon from './components/customIcons/WrenchIcon.vue';
import PunchClock from '@/components/customIcons/PunchClock.vue'
import TreeIcon from '@/components/customIcons/TreeIcon.vue';
import TaskPanel from '@/components/taskPanel/taskPanel.vue';


// displays modal to close tab if problem with logout api
import StatusAndOptionsMenu from './components/ui/StatusAndOptionsMenu.vue';
import LogoutModal from "./components/LogoutModal";

//import WarehouseQueue from "./components/warehouse/WarehouseQueue.vue"
//import RackBuildQueue from './components/warehouse/rackBuilds/RackBuildQueue.vue';
//import InvoiceQueue from "@/components/invoices/InvoiceQueue.vue"
import api from './api';
import FileInvoiceDollar from '@/components/customIcons/FileInvoiceDollar.vue'
import BoxSeamIcon from '@/components/customIcons/BoxSeamIcon.vue'
import QRCodeScanner from '@/components/logistics/QRCodeScanner.vue'
import ClipboardSearchIcon from '@/components/customIcons/ClipboardSearchIcon.vue'
import BoxSearchIcon from '@/components/customIcons/BoxSearchIcon.vue'
import ProjectSearchV2 from '@/components/projects/search/ProjectSearchV2.vue'
import EquipmentSearch from '@/components/equipment/EquipmentSearch.vue'
import DLSTrackerLogo from '@/components/ui/DLSTrackerLogo.vue'
import SignOutBar from '@/components/ui/SignOutBar.vue'
import AccountMenu from '@/components/ui/AccountMenu.vue'


export default {
  components: {
    SignOutBar,
    AccountMenu,
    LogoutModal,
    StatusAndOptionsMenu,
    CalendarIcon,
    UserGroupIcon,
    UsersIcon,
    OfficeBuildingIcon,
    CogIcon,
    DocumentsIcon,
    //WarehouseQueue,
    QrcodeIcon,
    DocumentTextIcon,
    //RackBuildQueue,
    ArrowCircleRightIcon,
    HomeIcon,
    ClipboardIcon,
    TruckIcon,
    SearchCircleIcon,
    CurrencyDollarIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    VanIcon,
    WrenchIcon,
    ServerIcon,
    BookOpenIcon,
    PunchClock,
    //InvoiceQueue,
    CashIcon,
    ClipboardCheckIcon,
    UserGroupSolidIcon,
    ChevronRightIcon,
    SearchIcon,
    PlusCircleIcon,
    LibraryIcon,
    PlusCircleIconOutline,
    TreeIcon,
    UserCircleIcon,
    TaskPanel,
    FileInvoiceDollar,
    BoxSeamIcon,
    QRCodeScanner,
    ClipboardSearchIcon,
    BoxSearchIcon,
    ProjectSearchV2,
    EquipmentSearch,
    WifiIcon,
    ColorSwatchIcon,
    DocumentAddIcon,
    DLSTrackerLogo
  },
  setup() {
    provide("global", global);
    const router = useRouter()
    const isLoading = ref(false);
    const showQRCodeScanner = ref(false);
    const showProjectSearch = ref(false);
    const showEquipmentSearch = ref(false);

    // global state
    const {
      authenticated,
      appStatus,
      appError,
      clearAppError,
      darkState,
      scrollState,
      setTheme,
      toggleTheme,
      setScrollBars,
      toggleScrollBars,
      setAiAssistCollapsed,
      initials,
      logout,
      isLogoutError,
      sideBar,
      toggleSideBar,
      closeSideBar,
      loggingOut,
      updateLocalAccount,
      plannedSignOut,
      setPlannedSignOut
    } = global;

    const route = useRoute()
    const hideHeader = computed(()=>route.meta?.hideHeader)

    // Planned sign-out: at zero this tab signs itself out (not the starter's). SignOutBar shows the countdown.
    let countdownTick = null
    watch(plannedSignOut, (plan) => {
      clearInterval(countdownTick)
      countdownTick = null
      if (plan) {
        countdownTick = setInterval(() => {
          if (Date.now() < plan.endsAt) { return }
          const starter = signOutStarter.value
          setPlannedSignOut(null)
          if (!starter) { logout() }
        }, 1000)
      }
    }, { immediate: true })
    onBeforeUnmount(() => clearInterval(countdownTick))
    const signOutStarter = computed(() => !!plannedSignOut.value?.byUserId && plannedSignOut.value.byUserId === String(authenticated.value?._id))

    const appErrorMessage = computed(() => {
      const err = appError.value
      if (!err) return ''
      return err instanceof Error ? err.message : String(err)
    })

    const clearAppErrorAndGoBack = () => {
      clearAppError()
      router.back()
    }

    const clearAppErrorAndReload = () => {
      clearAppError()
      window.location.reload()
    }

    const isPrime = computed(()=>{
      return authenticated.value.organization?.orgType && authenticated.value.organization?.orgType?.includes('primary')
    })

    const canSearchProjects = computed(()=>{
      return (authenticated.value.role?.accessModel?.projects > 0) || (authenticated.value.role?.accessModel?.projectView > 0)
    })

    // Mirrors the /equipment/search route gate. Warehouse and receiving roles search with or without project
    // access; rack-only roles are left out.
    const canSearchEquipment = computed(()=>{
      const am = authenticated.value.role?.accessModel || {}
      return (am.projects > 0) || (am.projectView > 0) || (am.warehouseQueue > 0) || (am.receiving > 0)
    })

    //const queues = computed(()=>{
    //  return [
    //    {component:InvoiceQueue,popper:'Invoices',condition:(authenticated.value.organization?.orgType && authenticated.value.organization?.orgType.includes('primary')) && authenticated.value.role?.accessModel?.invoices > 0,path:'/invoiceRequests'},
    //    {component:RackBuildQueue,popper:'Rack Builds',condition:(authenticated.value.organization?.orgType && authenticated.value.organization?.orgType.includes('primary')) && authenticated.value.role?.accessModel?.rackQueue > 0,path:'/rackBuildRequests'},
    //    {component:WarehouseQueue,popper:'Shipments',condition:(authenticated.value.organization?.orgType && authenticated.value.organization?.orgType.includes('primary')) && authenticated.value.role?.accessModel?.warehouseQueue > 0,path:'/shippingRequests'}
    //  ]
    //})

    const sideBarLinks = computed(()=>{
      return [
        {
          path:'/',
          icon:HomeIcon,
          label:'Home',
          condition:true
        },
        {
          path:'/projects',
          icon:ClipboardIcon,
          label:'Project List',
          section:'Projects',
          condition:authenticated.value.role?.accessModel?.projects > 0
        },
        //{
        //  path:'/projectList',
        //  icon:ClipboardIcon,
        //  label:'Project List V2',
        //  section:'Projects',
        //  condition:authenticated.value.role?.accessModel?.projects > 0
        //},
        {
          path:'/newProject',
          icon:DocumentAddIcon,
          label:'New Project',
          section:'Projects',
          condition:isPrime.value && authenticated.value.role?.accessModel?.projects > 2
        },
        {
          path:'#',
          icon:CurrencyDollarIcon,
          label:'Invoices',
          section:'Projects',
          condition:isPrime.value && (authenticated.value.role?.accessModel?.invoices > 0 || authenticated.value.role?.accessModel?.projects > 3) ,
          children:[
            {
              path:'/invoices',
              icon:CurrencyDollarIcon,
              label:'View Invoices',
              condition:isPrime.value && (authenticated.value.role?.accessModel?.invoices > 0 || authenticated.value.role?.accessModel?.projects > 3) 
            },
            {
              path:'/openInvoices',
              icon:CashIcon,
              label:'Open Invoices',
              condition:isPrime.value && (authenticated.value.role?.accessModel?.invoices > 0 || authenticated.value.role?.accessModel?.projects > 3)
            }
          ]
        },
        {
          path:'#',
          icon:BoxSeamIcon,
          label:'Logistics',
          section:'Operations',
          // Show only when at least one child is reachable (Catalog needs receiving, warehouseQueue or rackQueue;
          // Receiving needs receiving) so it never renders as an empty label.
          condition:isPrime.value && (authenticated.value.role?.accessModel?.receiving > 0 || authenticated.value.role?.accessModel?.warehouseQueue >= 1 || authenticated.value.role?.accessModel?.rackQueue > 0),
          children:[
            {
              path:'/deviceCatalog',
              icon:BookOpenIcon,
              label:'Catalog',
              condition:isPrime.value && (authenticated.value.role?.accessModel?.receiving > 0 || authenticated.value.role?.accessModel?.warehouseQueue > 0 || authenticated.value.role?.accessModel?.rackQueue > 0)
            },
            {
              path:'/receiveEquipment',
              icon:QrcodeIcon,
              label:'Receiving',
              condition:isPrime.value && authenticated.value.role?.accessModel?.receiving > 0
            }
          ]
        },
        {
          path:'#',
          icon:ServerIcon,
          label:'Equipment',
          section:'Operations',
          condition:isPrime.value,
          children:[
            {
              path:'/printLabels',
              icon:DocumentTextIcon,
              label:'Labels',
              condition:isPrime.value && (authenticated.value.role?.accessModel?.rackQueue > 0 || authenticated.value.role?.accessModel?.warehouseQueue > 0 ||  authenticated.value.role?.accessModel?.projects > 0 )
            },
            {
              path:'/tools',
              icon:WrenchIcon,
              label:'Tools',
              condition:isPrime.value && authenticated.value.role?.accessModel?.warehouseQueue > 0
            }
          ]
        },
        {
          path:'#',
          icon:CalendarIcon,
          label:'Scheduling',
          section:'Operations',
          condition:isPrime.value,
          children:[
            {
              path:'/scheduleResources',
              icon:UserGroupIcon,
              label:'Tech',
              condition:isPrime.value
            },
            //{
            //  path:'/techSchedule',
            //  icon:UserGroupIcon,
            //  label:'Tech (New)',
            //  condition:authenticated.value.organization?.orgType?.includes('primary')
            //},
            {
              path:'/shipmentSchedule',
              icon:TruckIcon,
              label:'Delivery',
              condition:isPrime.value && authenticated.value.role?.accessModel?.scheduling > 0
            },
          ]
        },
        {
          path:'/vehicleManagement',
          icon:VanIcon,
          label:'Vehicles',
          section:'Operations',
          condition:isPrime.value && (authenticated.value.role?.accessModel?.warehouseQueue > 0 || authenticated.value.role?.accessModel?.scheduling > 0)
        },
        {
          path:'#',
          icon:ClockIcon,
          label:'Time Mgmt.',
          section:'People',
          condition:(isPrime.value && authenticated.value.role?.accessModel?.approveTimesheets > 0) || authenticated.value.timesheet,
          children:[
            {
              path:'/myTimeclocks',
              icon:PunchClock,
              label:'My Timeclock',
              condition:authenticated.value.timesheet
            },
            {
              path:'/timeclockAdmin',
              icon:ClipboardCheckIcon,
              label:'Timeclock Admin',
              condition:authenticated.value.role?.accessModel?.approveTimesheets > 0
            },
            {
              path:'/approveTimecards',
              icon:CheckCircleIcon,
              label: 'Approve Timesheets',
              condition:authenticated.value.role?.accessModel?.approveTimesheets > 0,
            }
          ]
        },
        {        
          path:'#',
          icon:LibraryIcon,
          label:'HR/Payroll',
          section:'People',
          condition:isPrime.value && (authenticated.value.role?.accessModel?.payroll > 0 || authenticated.value.role?.accessModel?.scheduling > 0),
          children:[
            {
              path:'/payroll',
              icon:CurrencyDollarIcon,
              label: 'Payroll',
              condition:authenticated.value.role?.accessModel?.payroll > 0,
            },
            {
              path:'/specialRates',
              icon:CurrencyDollarIcon,
              label:'Special Rates',
              condition:false && authenticated.value.role?.accessModel?.payroll > 0,
            },
            {
              path:'/employeePTOInfo',
              icon:UserGroupSolidIcon,
              label:'Employee Details & PTO',
              condition:authenticated.value.role?.accessModel?.payroll > 0
            },
            {
              path:'/reports/PTOReports',
              icon:UserGroupSolidIcon,
              label:'PTO Report',
              condition:authenticated.value.role?.accessModel?.payroll > 0
            },
            {
              path:'/employeeSickTime',
              icon:PlusCircleIconOutline,
              label:'Sick Day Report',
              condition:authenticated.value.role?.accessModel?.payroll > 0
            },
            {
              path:'/closedVendorInvoices',
              icon:FileInvoiceDollar,
              label:'Accounts Payable',
              condition:authenticated.value.role?.accessModel?.payroll > 0
            },
            {
              path:'/holidays',
              icon:TreeIcon,
              label:'Holidays',
              condition:authenticated.value.role?.accessModel?.payroll > 0 || authenticated.value.role?.accessModel?.scheduling > 0
            },
            {
              path:'/ptoPlans',
              icon:CalendarIcon,
              label:'PTO Plans',
              condition:authenticated.value.role?.accessModel?.payroll > 0
            },
            {
              path:'/sickTime',
              icon:PlusCircleIcon,
              label:'Sick Day Plans',
              condition:authenticated.value.role?.accessModel?.payroll > 0
            },
          ]
        },
        {
          path:'/users',
          icon:UsersIcon,
          label:'Contacts',
          section:'People',
          condition:authenticated.value.role?.accessModel?.manageAccounts > 0
        },
        isPrime.value 
        ? 
        {
          path:'#',
          icon:OfficeBuildingIcon,
          label:'Companies',
          section:'People',
          condition:authenticated.value.role?.accessModel?.organizations > 0,
          children:[
            {
              path:'/companies',
              icon:OfficeBuildingSolidIcon,
              label:'Companies',
              condition:authenticated.value.role?.accessModel?.organizations > 0
            },
            {
              path:'/integratorInformationForms',
              icon:PlusIcon,
              label:'Integrator Info. Forms',
              condition:isPrime.value && authenticated.value.role?.accessModel?.organizations > 2
            },
            {
              path:'/newBranch',
              icon:PlusIcon,
              label:'Branch',
              condition:isPrime.value && authenticated.value.role?.accessModel?.organizations > 2
            },
            {
              path:'/newSubContractor',
              icon:PlusIcon,
              label:'Contractor',
              condition:isPrime.value && authenticated.value.role?.accessModel?.organizations > 2
            },
            //{
            //  path:'/organizations',
            //  icon:OfficeBuildingIcon,
            //  label:'Companies Old',
            //  condition:authenticated.value.role?.accessModel?.organizations > 0
            //},
          ]
        }
        :
        {
              path:'/companies',
              icon:OfficeBuildingIcon,
              label:'My Company',
              section:'People',
              condition:authenticated.value.role?.accessModel?.organizations > 0
        }
        ,
        {
          path:'/DataManagement',
          icon:CogIcon,
          label:'App Data',
          section:'Settings',
          condition:isPrime.value && authenticated.value.role?.accessModel?.appData > 0
        },
        {
          path:'#',
          icon:UserCircleIcon,
          label:'Admin',
          section:'Settings',
          condition:authenticated.value.role?.level > 9,
          children:[
            {
              path:'/admin/ActiveConnections',
              icon:WifiIcon,
              label:'Active Connections',
              condition:authenticated.value.role?.level > 9
            },
            {
              path:'/admin/ButtonsAndColors',
              icon:ColorSwatchIcon,
              label:'Buttons and Colors',
              condition:authenticated.value.role?.level > 9
            }
          ]
        },
      ]
    })

    // A group shows when at least one child does; a section shows when it has a visible item.
    const sideBarSections = computed(()=>{
      return [null,'Projects','Operations','People','Settings'].map(label=>({
        label,
        items:sideBarLinks.value
          .filter(item=>(item.section ?? null) === label && item.condition)
          .map(item=>item.children ? {...item,children:item.children.filter(child=>child.condition)} : item)
          .filter(item=>!item.children || item.children.length > 0)
      })).filter(section=>section.items.length > 0)
    })

    const openGroups = ref(new Set())

    // Matches on the resolved route record, as router-link does, so a URL typed in another case still counts.
    const groupHasCurrent = (item)=>{
      const current = route.matched[route.matched.length - 1]
      return !!current && !!item?.children?.some(child=>{
        const matched = router.resolve(child.path).matched
        return matched[matched.length - 1] === current
      })
    }

    const toggleGroup = (label)=>{
      const next = new Set(openGroups.value)
      if(next.has(label)) next.delete(label)
      else next.add(label)
      openGroups.value = next
    }

    const darkMode = ref(darkState)
    const scrollBars = ref(scrollState)

    axios.interceptors.response.use(
      (res) => res,
      (err) => {
        console.error(err?.response?.data?.error || err?.message || err);
        throw err;
      }
    );

    // The current page's group opens by itself.
    watch(()=>route.path,()=>{
      const next = new Set(openGroups.value)
      sideBarLinks.value.forEach(item=>{
        if(groupHasCurrent(item)) next.add(item.label)
      })
      openGroups.value = next
    },{immediate:true})

    onBeforeMount(()=>{
      //requestAuth()
      const savedTheme = localStorage.getItem('theme') || 'dark'
      setTheme(savedTheme)
      const savedScrollBarSetting = localStorage.getItem('scrollBars') || 'false'
      setScrollBars(savedScrollBarSetting)
      const savedAiCollapsed = localStorage.getItem('aiAssistCollapsed') || 'false'
      setAiAssistCollapsed(savedAiCollapsed)
    })

    let stopDayChangeNotifier = null;
    // Esc closes the phone menu overlay (below sm, where the scrim shows). Handled first and marked, so a SidePanel
    // under the menu stays open.
    const closeSideBarOnEscape = (e)=>{
      if (e.key !== 'Escape' || e.defaultPrevented || !sideBar.value) return
      if (!window.matchMedia('(max-width: 639px)').matches) return
      e.preventDefault()
      closeSideBar()
    }

    onMounted(()=>{
      window.addEventListener("data_change_from_socket",handleSocketChange)
      document.addEventListener('keydown', closeSideBarOnEscape)
      stopDayChangeNotifier = startDayChangeNotifier()
    })

    // Seed AI status from the in-memory cache before the first socket event arrives, so users joining mid-outage see the
    // topbar/AiPromptCard indicators immediately rather than after the next 5-min cron tick. Only once logged in — the
    // route needs a login, and nothing outside the app (login, external links) shows AI status. Best-effort; failure
    // leaves the default true.
    watch(() => authenticated.value?.loggedIn, (loggedIn) => {
      if (!loggedIn) return
      api.get('ai/status').then(res => {
        const ok = res?.data?.data?.ok
        if (typeof ok === 'boolean') global.setAppStatus('ai', ok)
      }).catch(() => {})
    }, { immediate: true })

    onBeforeUnmount(()=>{
      window.removeEventListener("data_change_from_socket",handleSocketChange)
      document.removeEventListener('keydown', closeSideBarOnEscape)
      if (stopDayChangeNotifier) stopDayChangeNotifier()
    })


    window.addEventListener("pagehide", () => {
      if (window.performance && authenticated.value.token) {
        const refreshKey = Math.random();
        sessionStorage.setItem("refresh", refreshKey);
        if (!localStorage.getItem("refresh")) {
          localStorage.setItem("refresh", refreshKey);
        }
      }
    });

    const updateLocalUser = async ()=>{
      await api
      .get('auth/loginUpdate')
      .then(res=>{
        if(typeof res.data?.data === 'object') {
          let newUserInfo = res.data.data
          newUserInfo.token = authenticated.value.token
          newUserInfo.refreshToken = authenticated.value.refreshToken
          updateLocalAccount(newUserInfo)
        }
      })
      .catch(err=>{
        console.error(err.response?.data?.error || err.message)
      })
    }

    const handleSocketChange = (e)=>{
      if(e.detail?.type) {
        if(e.detail.type === 'role' && authenticated.value.loggedIn) {
          // The event carries only the role id; the role itself comes from the server, never from another client.
          if(e.detail?.data?._id === authenticated.value.role?._id ) {
            updateLocalUser()
          }
        } else if(e.detail.type === 'user' && authenticated.value.loggedIn) {
          if(e.detail?.data?.userId === authenticated.value?._id) {
            updateLocalUser()
          }
        }
      } else {
        console.error('Bad data received from socket.')
      }
    }


     
     const openDetails = (target) =>{
      if(target) {
        target.nextElementSibling.classList.toggle('hidden')
      }
    }

    return {
      plannedSignOut,
      authenticated,
      appStatus,
      initials,
      logout,
      isLogoutError,
      //queues,
      toggleSideBar,
      sideBar,
      isLoading,
      loggingOut,
      openDetails,
      sideBarSections,
      openGroups,
      groupHasCurrent,
      toggleGroup,
      closeSideBar,
      darkMode,
      scrollBars,
      toggleTheme,
      setScrollBars,
      toggleScrollBars,
      hideHeader,
      isPrime,
      canSearchProjects,
      canSearchEquipment,
      showQRCodeScanner,
      showProjectSearch,
      showEquipmentSearch,
      appError,
      appErrorMessage,
      clearAppErrorAndGoBack,
      clearAppErrorAndReload
    };
  },
};
</script>

<style>
  #app {
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    /*font-family: Arial, Helvetica, sans-serif;*/
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    padding:0;
    margin:0;
  }

.sparkle {
  margin-left: 4px;
  vertical-align: middle;
  animation: flickerPulse 2s infinite ease-in-out;
}

@keyframes flickerPulse {
  0%, 100% { opacity: 0.9; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
}
</style>
